import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Boxes, Lock, LockOpen, Pencil, Plus, Trash2, Undo2, CheckCheck, Upload, Wallet } from 'lucide-react';

import { IMPRESSION_SUIVI_LOTS, lotsAPI, nouvelleCleLot } from '../../lib/lotsApi';
import {
  estAttribue,
  peutEtreRendu,
  peutPartirEnRetour,
  resteAConfirmer,
} from '../../lib/lotsFiltres';
import { indexerLignes, resumerConfirmation, resumerRetour } from '../../lib/lotsResultats';
import { formatDate, formatMontant, formatPrixUnitaire } from '../../lib/formats';
import { messageErreur } from '../../lib/erreurs';
import { formatVolume } from '../../lib/utils';
import { useRafraichirSur } from '../../hooks/useRafraichirSur';
import { useDroitsLots } from '../../hooks/useDroitsLots';
import { useImportListeLot } from '../../hooks/useImportListeLot';
import { ETATS_OUVERTS } from '../../lib/lotImportApi';
import { usePagination } from '../../hooks/usePagination';
import { PageHeader } from '../../components/ui/page-header';
import { PrintMenu } from '../../components/business/PrintMenu';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { StatutBadge } from '../../components/ui/StatutBadge';
import { EmptyState } from '../../components/ui/empty-state';
import { LoadingState } from '../../components/ui/loading-state';
import { BarrePagination } from '../../components/ui/pagination-bar';
import { toast } from '../../components/ui/sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import LotDialog from '../../components/lots/LotDialog';
import AjouterCamionsDialog from '../../components/lots/AjouterCamionsDialog';
import ModifierCamionDialog from '../../components/lots/ModifierCamionDialog';
import ConfirmerCamionsDialog from '../../components/lots/ConfirmerCamionsDialog';
import ImportListeDialog from '../../components/lots/ImportListeDialog';
import RetourDialog, { ACTION_DEPART_RETOUR, ACTION_RENDU } from '../../components/lots/RetourDialog';
import {
  BarreEtapes,
  BoutonEnvoiResponsable,
  CamionLigne,
  ChampRecherche,
  ChauffeurLigne,
  DatesLigne,
  EnteteDates,
  EtapeRetourLigne,
  LienTrajet,
  PanneauResultat,
  PastillesEtapes,
  PastillesRetours,
} from '../../components/lots/lotsUi';
import { produitsDuLot } from '../../lib/lotsProduits';

const compact = (texte) =>
  String(texte ?? '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const correspond = (ligne, terme) => {
  const mot = compact(terme);
  if (!mot) return true;
  return [
    ligne.tractor_plate,
    ligne.tank_plate,
    ligne.driver_label,
    ligne.driver_phone,
    ligne.loading_voucher_number,
    ligne.exit_document_number,
    ligne.loading_depot,
  ].some((valeur) => compact(valeur).includes(mot));
};

/** Un lot : en-tête, avancement par étape, camions, confirmation groupée et retours. */
const LotDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { peutEcrire, voitArgent } = useDroitsLots();

  const [detail, setDetail] = useState(null);
  const [chargement, setChargement] = useState(true);
  const [introuvable, setIntrouvable] = useState(false);
  const [erreur, setErreur] = useState(false);
  const [coches, setCoches] = useState(() => new Set());
  const [filtreEtape, setFiltreEtape] = useState('');
  const [filtreRetour, setFiltreRetour] = useState('');
  const [recherche, setRecherche] = useState('');
  const [resultat, setResultat] = useState(null);

  const [dialogueLot, setDialogueLot] = useState(false);
  const [dialogueAjout, setDialogueAjout] = useState(false);
  const [dialogueImport, setDialogueImport] = useState(false);
  const [dialogueConfirmation, setDialogueConfirmation] = useState(false);
  const [dialogueRetour, setDialogueRetour] = useState(null);
  const [ligneAModifier, setLigneAModifier] = useState(null);
  const [ligneARetirer, setLigneARetirer] = useState(null);
  const [actionEnCours, setActionEnCours] = useState(false);

  const charger = useCallback(
    async ({ silencieux = false } = {}) => {
      if (!silencieux) setChargement(true);
      try {
        const reponse = await lotsAPI.get(id, { skipToast: true });
        setDetail(reponse);
        setIntrouvable(false);
        setErreur(false);
      } catch (err) {
        console.error('Failed to load lot:', err);
        if (err?.response?.status === 404) setIntrouvable(true);
        else setErreur(true);
        if (!silencieux && err?.response?.status !== 404) toast.error(messageErreur(err, 'Impossible de charger le lot'));
      } finally {
        setChargement(false);
      }
    },
    [id],
  );

  useEffect(() => {
    setDetail(null);
    setCoches(new Set());
    setResultat(null);
    charger();
  }, [charger]);

  useRafraichirSur(['lots', 'trajets'], charger);

  // Import de la liste des camions : le travail ouvert de ce lot, suivi tant que le serveur travaille.
  const { job: travailImport, setJob: setTravailImport } = useImportListeLot(id, {
    actif: peutEcrire,
    onTermine: () => charger({ silencieux: true }),
  });

  // Arrivée depuis la création du lot avec une liste jointe: le brouillon s'ouvre tout de suite.
  const { importer: importDemande, job: travailDepose } = location.state || {};
  useEffect(() => {
    if (!importDemande || !peutEcrire) return;
    if (travailDepose) setTravailImport(travailDepose);
    setDialogueImport(true);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [importDemande, peutEcrire]);

  const lot = detail?.lot;
  const progress = detail?.progress;
  const lignes = useMemo(() => detail?.lines || [], [detail]);
  const argent = detail?.money;
  const clos = lot?.status === 'CLOS';

  // Une ligne retirée ou mise à jour ailleurs ne reste pas cochée.
  useEffect(() => {
    setCoches((avant) => {
      const presentes = new Set(lignes.map((ligne) => ligne.id));
      const suite = new Set([...avant].filter((identifiant) => presentes.has(identifiant)));
      return suite.size === avant.size ? avant : suite;
    });
  }, [lignes]);

  const lignesVisibles = useMemo(
    () =>
      lignes.filter(
        (ligne) =>
          (!filtreEtape || ligne.stage === filtreEtape) &&
          (!filtreRetour || ligne.return_status === filtreRetour) &&
          correspond(ligne, recherche),
      ),
    [lignes, filtreEtape, filtreRetour, recherche],
  );
  const pagination = usePagination(lignesVisibles, {
    reinitialiser: `${filtreEtape}|${filtreRetour}|${recherche}`,
  });

  const cochees = useMemo(() => lignes.filter((ligne) => coches.has(ligne.id)), [lignes, coches]);
  const aConfirmer = useMemo(() => cochees.filter(estAttribue), [cochees]);
  const aPartir = useMemo(() => cochees.filter(peutPartirEnRetour), [cochees]);
  const aRendre = useMemo(() => cochees.filter(peutEtreRendu), [cochees]);

  const toutEstCoche = lignesVisibles.length > 0 && lignesVisibles.every((ligne) => coches.has(ligne.id));
  const basculer = (identifiant) =>
    setCoches((avant) => {
      const suite = new Set(avant);
      if (suite.has(identifiant)) suite.delete(identifiant);
      else suite.add(identifiant);
      return suite;
    });
  const toutCocher = (valeur) =>
    setCoches((avant) => {
      const suite = new Set(avant);
      lignesVisibles.forEach((ligne) => (valeur ? suite.add(ligne.id) : suite.delete(ligne.id)));
      return suite;
    });

  const apresEcriture = () => charger({ silencieux: true });

  const changerStatut = async () => {
    if (actionEnCours || !lot) return;
    setActionEnCours(true);
    try {
      const suite = await lotsAPI.update(lot.id, { status: clos ? 'OUVERT' : 'CLOS' }, nouvelleCleLot('lot-statut'));
      if (suite?.lot) setDetail(suite);
      toast.success(clos ? 'Lot rouvert' : 'Lot clos');
    } catch (err) {
      toast.error(messageErreur(err, 'Le lot n’a pas pu être modifié'));
    } finally {
      setActionEnCours(false);
    }
  };

  const retirer = async () => {
    if (!ligneARetirer || actionEnCours) return;
    setActionEnCours(true);
    try {
      await lotsAPI.removeTruck(lot.id, ligneARetirer.id);
      toast.success('Camion retiré du lot');
      setLigneARetirer(null);
      await apresEcriture();
    } catch (err) {
      toast.error(messageErreur(err, 'Le camion n’a pas pu être retiré'));
    } finally {
      setActionEnCours(false);
    }
  };

  const parId = useMemo(() => indexerLignes(lignes), [lignes]);

  const surConfirmation = (reponse) => {
    setResultat(resumerConfirmation(reponse));
    setCoches(new Set());
    apresEcriture();
  };
  const surRetour = (reponse) => {
    setResultat(resumerRetour(reponse, parId));
    setCoches(new Set());
    apresEcriture();
  };
  const surAjout = () => apresEcriture();

  if (chargement && !detail) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <LoadingState shape="detail" />
      </div>
    );
  }

  if (introuvable || (!detail && erreur)) {
    return (
      <div className="mx-auto w-full max-w-page space-y-5 px-4 py-5 md:px-6 md:py-6 lg:px-8">
        <EmptyState
          variant={introuvable ? 'empty' : 'error'}
          icon={Boxes}
          title={introuvable ? 'Ce lot est introuvable' : 'Le lot n’a pas pu être chargé'}
          description={introuvable ? 'Il a peut-être été retiré.' : 'Réessayez dans un instant.'}
          actionLabel={introuvable ? 'Voir les lots' : 'Réessayer'}
          onAction={() => (introuvable ? navigate('/lots/liste') : charger())}
        />
      </div>
    );
  }

  if (!lot) return null;

  const reste = resteAConfirmer(progress);
  const prixAbsent = voitArgent && (lot.price_per_liter === null || lot.price_per_liter === undefined);
  const parametresImpression = { lot_id: lot.id };
  const importOuvert = Boolean(travailImport && ETATS_OUVERTS.includes(travailImport.status));
  const alertesImport = importOuvert
    ? [
        {
          niveau: 'attention',
          court: travailImport.status === 'draft' ? 'liste à relire' : 'import en cours',
          texte:
            travailImport.status === 'draft'
              ? 'Une liste de camions importée attend votre relecture.'
              : 'Une liste de camions est en cours d’import.',
          action: { label: 'Ouvrir', onClick: () => setDialogueImport(true) },
        },
      ]
    : [];

  return (
    <div className="mx-auto w-full max-w-page space-y-4 px-4 py-5 md:px-6 md:py-6 lg:px-8" data-testid="lot-detail">
      <PageHeader
        eyebrow="Lots attribués"
        title={lot.reference}
        backTo="/lots/liste"
        backLabel="Lots"
        meta={<StatutBadge domaine="lot_statut" code={lot.status} />}
        stats={[
          { label: 'Attribués', valeur: progress?.attributed || 0, testid: 'lot-stat-attribues' },
          ...(lot.promised_trucks
            ? [
                { label: 'Total', valeur: lot.promised_trucks, testid: 'lot-stat-promis' },
                {
                  label: 'À attribuer',
                  valeur: lot.to_attribute || 0,
                  ton: lot.to_attribute > 0 ? 'attention' : 'positif',
                  testid: 'lot-stat-a-attribuer',
                },
              ]
            : []),
          { label: 'Confirmés', valeur: progress?.confirmed || 0, ton: 'positif', testid: 'lot-stat-confirmes' },
          {
            label: 'À confirmer',
            valeur: reste,
            ton: reste > 0 ? 'attention' : 'positif',
            testid: 'lot-stat-reste',
          },
          { label: 'Chargé', valeur: Math.round(progress?.loaded_liters || 0), unite: 'L', testid: 'lot-stat-charge' },
        ]}
        alertes={[
          ...(prixAbsent && peutEcrire
            ? [
                {
                  niveau: 'attention',
                  court: 'prix au litre à saisir',
                  texte: 'Le prix au litre du lot n’est pas saisi : il est recopié sur chaque trajet confirmé.',
                  action: { label: 'Modifier le lot', onClick: () => setDialogueLot(true) },
                },
              ]
            : []),
          ...(peutEcrire ? alertesImport : []),
        ]}
        actions={
          <>
            <PrintMenu
              label="Imprimer"
              items={[
                {
                  label: 'Suivi du lot',
                  filename: `suivi-${lot.reference}`,
                  url: IMPRESSION_SUIVI_LOTS,
                  formats: ['pdf', 'xlsx', 'csv'],
                  params: parametresImpression,
                },
              ]}
            />
            {peutEcrire ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogueAjout(true)}
                disabled={clos}
                data-testid="lot-ajouter-camions"
              >
                <Plus className="h-4 w-4" aria-hidden="true" />
                Ajouter des camions
              </Button>
            ) : null}
            {peutEcrire ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setDialogueImport(true)}
                disabled={clos && !importOuvert}
                data-testid="lot-importer"
              >
                <Upload className="h-4 w-4" aria-hidden="true" />
                Importer une liste
              </Button>
            ) : null}
            {voitArgent ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/lots/${lot.id}/paiements`)}
                data-testid="lot-paiements"
              >
                <Wallet className="h-4 w-4" aria-hidden="true" />
                Paiements
              </Button>
            ) : null}
            {peutEcrire ? (
              <Button variant="outline" size="sm" onClick={() => setDialogueLot(true)} data-testid="lot-modifier">
                <Pencil className="h-4 w-4" aria-hidden="true" />
                Modifier
              </Button>
            ) : null}
            {peutEcrire ? (
              <Button
                variant="outline"
                size="sm"
                onClick={changerStatut}
                disabled={actionEnCours}
                data-testid="lot-statut-action"
              >
                {clos ? <LockOpen className="h-4 w-4" aria-hidden="true" /> : <Lock className="h-4 w-4" aria-hidden="true" />}
                {clos ? 'Rouvrir' : 'Clôturer'}
              </Button>
            ) : null}
            {/* En dernier: l'en-tête garde les premières actions en vue, le reste va dans « Plus ». */}
            {peutEcrire ? <BoutonEnvoiResponsable params={parametresImpression} testid="lot-send-manager" /> : null}
          </>
        }
      />

      {/* L'identité du lot en une ligne. */}
      <section
        className="flex flex-wrap items-center gap-x-5 gap-y-1 rounded-xl border border-border bg-card px-4 py-2.5 text-sm"
        data-testid="lot-identite"
      >
        <span>
          <span className="text-muted-foreground">Transporteur </span>
          <span className="font-semibold text-foreground">{lot.transporter_name || '—'}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Produit </span>
          <span className="font-semibold text-foreground">{produitsDuLot(lot)}</span>
        </span>
        <span>
          <span className="text-muted-foreground">Attribué le </span>
          <span className="font-semibold text-foreground">{formatDate(lot.attributed_at)}</span>
        </span>
        {lot.default_depot ? (
          <span>
            <span className="text-muted-foreground">Dépôt </span>
            <span className="font-semibold text-foreground">{lot.default_depot}</span>
          </span>
        ) : null}
        {voitArgent && lot.price_per_liter !== null && lot.price_per_liter !== undefined ? (
          <span data-testid="lot-prix-litre">
            <span className="text-muted-foreground">Prix </span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatPrixUnitaire(lot.price_per_liter, lot.currency)}/L
            </span>
          </span>
        ) : null}
        {argent && argent.loaded_value !== null && argent.loaded_value !== undefined ? (
          <span data-testid="lot-valeur-chargee">
            <span className="text-muted-foreground">Valeur chargée </span>
            <span className="font-semibold tabular-nums text-foreground">
              {formatMontant(argent.loaded_value, argent.currency)}
            </span>
          </span>
        ) : null}
        {lot.notes ? <span className="min-w-0 truncate text-muted-foreground">{lot.notes}</span> : null}
      </section>

      <section className="space-y-2" aria-label="Avancement du lot">
        <BarreEtapes stages={progress?.stages} />
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5">
          <PastillesEtapes stages={progress?.stages} actif={filtreEtape} onChoisir={setFiltreEtape} />
          <PastillesRetours returns={progress?.returns} actif={filtreRetour} onChoisir={setFiltreRetour} />
        </div>
      </section>

      <PanneauResultat resume={resultat} onFermer={() => setResultat(null)} />

      {/* Recherche étroite ; les pastilles ci-dessus filtrent par étape ou par retour. */}
      <div className="flex flex-wrap items-center gap-2">
        <ChampRecherche
          valeur={recherche}
          onChange={setRecherche}
          placeholder="Plaque, chauffeur, N° de bon…"
          label="Rechercher un camion du lot"
          testid="lot-recherche"
        />
        <span className="text-sm tabular-nums text-muted-foreground" data-testid="lot-compte">
          {lignesVisibles.length} camion{lignesVisibles.length > 1 ? 's' : ''}
        </span>
      </div>

      {peutEcrire && cochees.length > 0 ? (
        <div
          className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2"
          data-testid="lot-barre-selection"
        >
          <span className="text-sm font-medium">
            {cochees.length} camion{cochees.length > 1 ? 's' : ''} coché{cochees.length > 1 ? 's' : ''}
          </span>
          <Button
            size="sm"
            onClick={() => setDialogueConfirmation(true)}
            disabled={aConfirmer.length === 0 || clos}
            data-testid="lot-confirmer"
          >
            <CheckCheck className="h-4 w-4" aria-hidden="true" />
            Confirmer ({aConfirmer.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogueRetour(ACTION_DEPART_RETOUR)}
            disabled={aPartir.length === 0}
            data-testid="lot-depart-retour"
          >
            <Undo2 className="h-4 w-4" aria-hidden="true" />
            Départ retour ({aPartir.length})
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setDialogueRetour(ACTION_RENDU)}
            disabled={aRendre.length === 0}
            data-testid="lot-rendu"
          >
            Rendu ({aRendre.length})
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setCoches(new Set())}>
            Tout décocher
          </Button>
        </div>
      ) : null}

      {lignes.length === 0 ? (
        <EmptyState
          icon={Boxes}
          title="Aucun camion dans ce lot"
          aide="Ajoutez les plaques des camions attribués par le transporteur : collées depuis Excel ou saisies."
          actionLabel={peutEcrire && !clos ? 'Ajouter des camions' : undefined}
          onAction={peutEcrire && !clos ? () => setDialogueAjout(true) : undefined}
        />
      ) : lignesVisibles.length === 0 ? (
        <EmptyState variant="filtered" title="Aucun résultat" description="Aucun camion ne correspond à cette sélection." />
      ) : (
        <Card className="card-base">
          <CardContent className="p-0">
            <Table compact>
              <TableHeader>
                <TableRow>
                  {peutEcrire ? (
                    <TableHead className="w-10">
                      <Checkbox
                        checked={toutEstCoche}
                        onCheckedChange={(valeur) => toutCocher(Boolean(valeur))}
                        aria-label="Cocher tous les camions de la sélection"
                        data-testid="lot-cocher-tout"
                      />
                    </TableHead>
                  ) : null}
                  <TableHead>Camion</TableHead>
                  <TableHead>Chauffeur</TableHead>
                  <TableHead className="text-right">Prévu</TableHead>
                  <TableHead className="text-right">Chargé</TableHead>
                  <TableHead>Étape</TableHead>
                  <TableHead className="hidden xl:table-cell">Dépôt</TableHead>
                  <TableHead>N° bon</TableHead>
                  <TableHead>
                    <EnteteDates />
                  </TableHead>
                  <TableHead>Trajet</TableHead>
                  {peutEcrire ? <TableHead className="text-right">Actions</TableHead> : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagination.lignes.map((ligne) => (
                  <TableRow
                    key={ligne.id}
                    data-state={coches.has(ligne.id) ? 'selected' : undefined}
                    data-testid={`lot-ligne-${ligne.id}`}
                  >
                    {peutEcrire ? (
                      <TableCell className="w-10">
                        <Checkbox
                          checked={coches.has(ligne.id)}
                          onCheckedChange={() => basculer(ligne.id)}
                          aria-label={`Cocher le camion ${ligne.tractor_plate}`}
                          data-testid={`lot-cocher-${ligne.id}`}
                        />
                      </TableCell>
                    ) : null}
                    <TableCell>
                      <CamionLigne ligne={ligne} />
                    </TableCell>
                    <TableCell>
                      <ChauffeurLigne ligne={ligne} />
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {ligne.expected_quantity ? formatVolume(ligne.expected_quantity) : '—'}
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-right tabular-nums">
                      {ligne.loaded_quantity ? formatVolume(ligne.loaded_quantity) : '—'}
                    </TableCell>
                    <TableCell>
                      <EtapeRetourLigne ligne={ligne} />
                    </TableCell>
                    <TableCell className="hidden whitespace-nowrap xl:table-cell">{ligne.loading_depot || '—'}</TableCell>
                    <TableCell className="whitespace-nowrap">{ligne.loading_voucher_number || '—'}</TableCell>
                    <TableCell>
                      <DatesLigne ligne={ligne} />
                    </TableCell>
                    <TableCell>
                      <LienTrajet tripId={ligne.trip_id} />
                    </TableCell>
                    {peutEcrire ? (
                      <TableCell className="text-right">
                        {estAttribue(ligne) ? (
                          <span className="inline-flex gap-0.5">
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => setLigneAModifier(ligne)}
                              aria-label={`Modifier le camion ${ligne.tractor_plate}`}
                              data-testid={`lot-modifier-${ligne.id}`}
                            >
                              <Pencil className="h-4 w-4" aria-hidden="true" />
                            </Button>
                            <Button
                              type="button"
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8"
                              onClick={() => setLigneARetirer(ligne)}
                              aria-label={`Retirer le camion ${ligne.tractor_plate}`}
                              data-testid={`lot-retirer-${ligne.id}`}
                            >
                              <Trash2 className="h-4 w-4" aria-hidden="true" />
                            </Button>
                          </span>
                        ) : null}
                      </TableCell>
                    ) : null}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {lignesVisibles.length > 0 ? <BarrePagination pagination={pagination} nom="camions" /> : null}

      {peutEcrire ? (
        <>
          <LotDialog
            open={dialogueLot}
            onOpenChange={setDialogueLot}
            lot={lot}
            camionsConfirmes={progress?.confirmed || 0}
            voitArgent={voitArgent}
            onSaved={apresEcriture}
          />
          <AjouterCamionsDialog
            open={dialogueAjout}
            onOpenChange={setDialogueAjout}
            lotId={lot.id}
            reference={lot.reference}
            onDone={surAjout}
          />
          <ImportListeDialog
            open={dialogueImport}
            onOpenChange={setDialogueImport}
            lotId={lot.id}
            reference={lot.reference}
            job={travailImport}
            onJob={setTravailImport}
          />
          <ModifierCamionDialog
            open={Boolean(ligneAModifier)}
            onOpenChange={(ouvert) => !ouvert && setLigneAModifier(null)}
            lotId={lot.id}
            ligne={ligneAModifier}
            onDone={apresEcriture}
          />
          <ConfirmerCamionsDialog
            open={dialogueConfirmation}
            onOpenChange={setDialogueConfirmation}
            lotId={lot.id}
            reference={lot.reference}
            depotParDefaut={lot.default_depot}
            lignes={aConfirmer}
            onDone={surConfirmation}
          />
          <RetourDialog
            open={Boolean(dialogueRetour)}
            onOpenChange={(ouvert) => !ouvert && setDialogueRetour(null)}
            action={dialogueRetour || ACTION_DEPART_RETOUR}
            lineIds={(dialogueRetour === ACTION_RENDU ? aRendre : aPartir).map((ligne) => ligne.id)}
            onDone={surRetour}
          />
          <AlertDialog open={Boolean(ligneARetirer)} onOpenChange={(ouvert) => !ouvert && setLigneARetirer(null)}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Retirer ce camion du lot ?</AlertDialogTitle>
                <AlertDialogDescription>
                  {ligneARetirer ? `Le camion ${ligneARetirer.tractor_plate} n’est pas encore confirmé : il disparaît de la liste.` : ''}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={actionEnCours}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  onClick={(evenement) => {
                    evenement.preventDefault();
                    retirer();
                  }}
                  disabled={actionEnCours}
                  data-testid="lot-retirer-confirmer"
                >
                  Retirer
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </>
      ) : null}
    </div>
  );
};

export default LotDetailPage;
